import { Link } from "react-router-dom";
import type { Business } from "@/types/directory";
import { computeOpenNow } from "@/utils/hours";
import { isPaidPlan } from "@/utils/plans";
import Badge from "@/components/base/Badge";
import LockedFeature from "@/components/base/LockedFeature";
import RatingStars from "@/components/base/RatingStars";

interface SponsoredListingsProps {
  businesses: Business[];
  loading?: boolean;
}

function SponsoredRowSkeleton() {
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 sm:flex-row">
      <div className="h-44 w-full shrink-0 animate-pulse rounded-md bg-background-200 sm:h-32 sm:w-56" />
      <div className="flex flex-1 flex-col gap-3">
        <div className="h-3 w-28 animate-pulse rounded-full bg-background-200" />
        <div className="h-4 w-3/5 animate-pulse rounded-full bg-background-200" />
        <div className="h-3 w-4/5 animate-pulse rounded-full bg-background-200" />
        <div className="mt-auto h-4 w-32 animate-pulse rounded-full bg-background-200" />
      </div>
    </div>
  );
}

function SponsoredRow({ business }: { business: Business }) {
  const openNow = computeOpenNow(business.hours, business.openNow);
  const paid = isPaidPlan(business.plan);

  return (
    <article className="group flex flex-col gap-4 overflow-hidden rounded-lg border border-accent-300 bg-accent-50/70 p-4 transition-colors duration-200 hover:border-accent-500 sm:flex-row">
      <Link
        to={`/business/${business.slug}`}
        className="relative block h-44 w-full shrink-0 overflow-hidden rounded-md sm:h-32 sm:w-56"
        aria-label={`View ${business.name}`}
      >
        <img
          src={business.cover}
          alt={`${business.name} - ${business.subcategory}`}
          title={`${business.name} - ${business.subcategory}`}
          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute left-2 top-2 flex flex-wrap gap-1">
          <Badge tone="accent" icon="ri-megaphone-line">Sponsored</Badge>
        </div>
      </Link>

      <div className="flex flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`/category/${business.categorySlug}`}
            className="text-xs font-semibold uppercase tracking-wider text-secondary-700 transition-colors hover:text-primary-700"
          >
            {business.subcategory}
          </Link>
          {business.verified ? (
            <Badge tone="secondary" icon="ri-verified-badge-fill">Verified</Badge>
          ) : null}
          {paid ? (
            openNow ? (
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-semibold text-secondary-800">
                <span className="h-1.5 w-1.5 rounded-full bg-secondary-500" />
                Open Now
              </span>
            ) : null
          ) : (
            <LockedFeature compact label="Live hours" />
          )}
        </div>

        <h3 className="mt-1.5 font-heading text-base font-bold leading-snug text-foreground-950">
          <Link
            to={`/business/${business.slug}`}
            className="transition-colors hover:text-primary-700"
          >
            {business.name}
          </Link>
        </h3>

        <p className="mt-1 flex items-start gap-1.5 text-xs text-foreground-500">
          <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center">
            <i className="ri-map-pin-line text-[13px] text-red-500" />
          </span>
          {business.address}
        </p>

        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
          {business.tagline}
        </p>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <RatingStars rating={business.rating} count={business.reviewCount} />
          <span className="hidden h-3.5 w-px bg-background-200 sm:block" />
          <span className="hidden flex-wrap gap-1.5 sm:flex">
            {business.tags.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-background-100 px-2.5 py-1 text-xs font-medium text-foreground-600"
              >
                {tag}
              </span>
            ))}
          </span>
        </div>

        <div className="mt-4 flex items-center gap-2 border-t border-background-200 pt-4 sm:hidden">
          <a
            href={`tel:${business.phone.replace(/\s/g, "")}`}
            className="inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-2.5 text-xs font-semibold text-background-50 transition-colors duration-200 hover:bg-primary-600"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-phone-line text-sm" />
            </span>
            Call
          </a>
          <Link
            to={`/business/${business.slug}`}
            className="inline-flex flex-1 items-center justify-center whitespace-nowrap rounded-md border border-background-300 px-3 py-2.5 text-xs font-semibold text-foreground-700 transition-colors duration-200 hover:border-primary-300 hover:text-primary-700"
          >
            View Details
          </Link>
        </div>
      </div>

      <div className="hidden shrink-0 flex-col justify-center gap-2 sm:flex sm:w-32">
        <a
          href={`tel:${business.phone.replace(/\s/g, "")}`}
          className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-2.5 text-xs font-semibold text-background-50 transition-colors duration-200 hover:bg-primary-600"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-phone-line text-sm" />
          </span>
          Call
        </a>
        <a
          href={`https://wa.me/${business.whatsapp.replace(/[^0-9]/g, "")}`}
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-500 px-3 py-2.5 text-xs font-semibold text-background-50 transition-colors duration-200 hover:bg-secondary-600"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-whatsapp-line text-sm" />
          </span>
          WhatsApp
        </a>
        <Link
          to={`/business/${business.slug}`}
          className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-background-300 px-3 py-2.5 text-xs font-semibold text-foreground-700 transition-colors duration-200 hover:border-primary-300 hover:text-primary-700"
        >
          View Details
        </Link>
      </div>
    </article>
  );
}

export default function SponsoredListings({
  businesses,
  loading = false,
}: SponsoredListingsProps) {
  if (!loading && businesses.length === 0) return null;

  return (
    <section className="mb-6" aria-label="Sponsored listings">
      <div className="flex flex-wrap items-center gap-3 border-b border-background-200 pb-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-500 text-foreground-950">
          <i className="ri-megaphone-line text-lg" />
        </span>
        <div>
          <h2 className="font-heading text-base font-bold text-foreground-950 md:text-lg">
            Sponsored Listings
          </h2>
          <p className="text-xs text-foreground-500">Promoted businesses in this category</p>
        </div>
        {!loading && businesses.length > 0 ? (
          <span className="ml-auto inline-flex items-center whitespace-nowrap rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-900">
            {businesses.length} promoted
          </span>
        ) : null}
      </div>

      <div className="mt-4 space-y-4">
        {loading
          ? Array.from({ length: 2 }).map((_, index) => <SponsoredRowSkeleton key={index} />)
          : businesses.map((business) => <SponsoredRow key={business.id} business={business} />)}
      </div>
    </section>
  );
}